import { NextPage } from 'next'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'

import { useFocusActionState } from 'src/providers/FocusActionProvider'
import { useUser } from 'src/providers/UserProvider'

import { InternalLink } from 'src/components/atoms/Link'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { Note } from 'src/components/atoms/Note'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'
import { Page } from 'src/components/layouts/Page'
import { AccountForm } from 'src/components/organisms/forms/AccountForm'

import { useGetActiveInviteByEmailQuery } from 'src/config/generated/graphql'
import { homePath, inviteAccountUserPath } from 'src/config/paths'
import { AccountType } from 'src/types/account'
import { withAuthRequired } from 'src/utils/auth'

const DashboardZeroState = () => {
  const router = useRouter()
  const params = useSearchParams()
  const { user, isLoading } = useUser()
  const returnTo = params.get('returnTo')
  const [accountType, setAccountType] = useState<string>('')

  const { data: invitationData } = useGetActiveInviteByEmailQuery({
    variables: {
      email: user?.Email!
    },
    skip: !user?.Email
  })

  useEffect(() => {
    // Only run this on mount - otherwise we run the risk of race conditions when the user updates...
    if (isLoading) return
    const requiresAccountSetup = !!user && !user?.Account
    if (!requiresAccountSetup) router.replace(returnTo || homePath)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  return (
    <div className='p-0! pt-4! max-w-full md:w-[800px] w-full md:mx-auto md:pb-24! min-h-dvh'>
      <div className='flex flex-col items-center gap-[10px] md:gap-5 mb-5 md:mb-10 text-center'>
        <TypographyRebrand variant='h2' color='forestDeep'>
          <strong>
            {accountType === AccountType.Business
              ? 'Set up your business account'
              : 'Set up your account'}
          </strong>
        </TypographyRebrand>
        <TypographyRebrand variant='introduction' color='forestDeep'>
          All fields are required.
        </TypographyRebrand>
      </div>
      {invitationData?.Invite.length === 1 ? (
        <Note variant='solid'>
          {invitationData?.Invite[0]?.Account.Name} has invited you to join
          their account.{' '}
          <InternalLink
            href={inviteAccountUserPath({
              queryParams: {
                code: invitationData?.Invite[0]?.Code
              }
            })}
          >
            View and accept the invitation here.
          </InternalLink>
        </Note>
      ) : null}
      <AccountForm onAccountTypeChange={setAccountType} />
    </div>
  )
}

const OnboardPage: NextPage = () => {
  const { user } = useUser()
  const { setFocussedHeader } = useFocusActionState()

  // Match /donate — show the focussed header (logo + close) instead of the
  // full nav.
  useEffect(() => {
    setFocussedHeader()
  }, [setFocussedHeader])

  const content = useMemo(() => {
    if (!user) {
      return <LoadingSpinner isLoading />
    }
    return <DashboardZeroState />
  }, [user])

  return (
    <Page title='Account set up' isFullWidth>
      <div className='2xl:max-w-[1220px] flex flex-col gap-5 sm:gap-10 mx-4 md:mx-[60px] lg:mx-11 xl:mx-[110px] 2xl:mx-auto py-[30px] md:py-12 xl:py-[60px]'>
        {content}
      </div>
    </Page>
  )
}

export default withAuthRequired(OnboardPage)
